import React, { useEffect, useRef, useState } from "react";
import {
  Table,
  Row,
  Col,
  Card,
  CardBody,
  Container,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import { TablePagination } from "../../Components/Pagination";
import { showToast } from "../../Components/Toast";
import Skeleton from "react-loading-skeleton";
import { getAllUsers, updateUser, addUser } from "../../API/Backend";
import { getUserInfo } from "../../Utilities/Auth";
import { useClient } from "../../Utilities/ClientContext";
import validator from "validator";

export default function UserManagement() {
  const { clients } = useClient();
  // Client admins can only add users for their own client (enforced by the backend)
  const isClientAdmin = Boolean(getUserInfo()?.user?.clientName);
  const clientLabel = (clientName) =>
    clientName
      ? clients.find((c) => c.clientName === clientName)?.companyName || clientName
      : "All clients";
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [update, setUpdate] = useState(0);
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [keyword, setKeyword] = useState("");
  const keywordRef = useRef("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    password: "",
    phoneNumber: "",
    clientName: "",
  });
  const [showPassword, setShowPassword] = useState(false);

  const generatePassword = () => {
    const charset =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < 12; i++) {
      const randomChar = charset[Math.floor(Math.random() * charset.length)];
      password += randomChar;
    }
    setNewUser({ ...newUser, password });
  };

  const isStrongPassword = (password) => {
    return /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*?&]{8,}$/.test(password);
  };

  useEffect(() => {
    fetchUsers();
  }, [update]);

  const fetchUsers = (limit = 10, offset = 0, keyword = "") => {
    setLoading(true);
    setUsers([]);
    getAllUsers({ limit, offset, ...(keyword && { search: keyword }) })
      .then(({ data }) => {
        if (
          data &&
          data.success &&
          data.data &&
          Array.isArray(data.data.users)
        ) {
          setUsers(data.data.users);
          setCount(data.data.count || 0);
        } else {
          showToast(
            "error",
            "Request Failed",
            data?.message || "Invalid response format"
          );
        }
      })
      .catch((err) => {
        showToast("error", "Request Failed", err.message || "Server error");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onPaginationChange = (currentPage) => {
    if (page !== currentPage) {
      setPage(currentPage);
      fetchUsers(limit, limit * (currentPage - 1), keyword);
    }
  };

  const searchByKeyword = (e) => {
    e.preventDefault();
    setKeyword(keywordRef.current.value);
    fetchUsers(10, 0, keywordRef.current.value);
  };

  const openUserModal = (user) => {
    setSelectedUser(user);
    setModalOpen(true);
  };

  const handleUpdate = () => {
    const updatedData = {
      name: selectedUser.name,
      email: selectedUser.email,
      phoneNumber: selectedUser.phoneNumber,
      isActive: selectedUser.isActive,
      ...(!isClientAdmin && { clientName: selectedUser.clientName || "" }),
    };

    updateUser(selectedUser._id, updatedData)
      .then(({ data }) => {
        if (data.success) {
          showToast("success", "Updated", "User updated successfully");
          setModalOpen(false);
          setUpdate((prev) => prev + 1);
        } else {
          showToast("error", "Error", data.message);
        }
      })
      .catch((err) => {
        showToast("error", "Error", err.message);
      });
  };
  const handleAddUser = async () => {
    const { name, email, password, phoneNumber, clientName } = newUser;

    if (!name || !email || !password || !phoneNumber) {
      showToast("error", "Please fill in all fields");
      return;
    }

    if (!validator.isEmail(email)) {
      showToast("error", "Invalid email format");
      return;
    }

    if (!isStrongPassword(password)) {
      showToast(
        "error",
        "Password must be at least 8 characters long and include uppercase, lowercase, number, and special character."
      );
      return;
    }

    if (!/^\d{6,15}$/.test(phoneNumber)) {
      showToast("error", "Invalid phone number format");
      return;
    }

    try {
      setLoading(true); // Disable submit while loading
      const response = await addUser({ name, email, password, phoneNumber, clientName });
      showToast("success", "User added successfully");
      setAddModalOpen(false);
      setNewUser({ name: "", email: "", password: "", phoneNumber: "", clientName: "" });
      fetchUsers(); // refresh list
    } catch (error) {
      showToast("error", "Failed to add user");
      console.error(error);
    } finally {
      setLoading(false); // Re-enable submit button
    }
  };

  return (
    <Container fluid>
      <Row className="align-items-center mb-3">
        <Col>
          <h5 className="page-title">User Management</h5>
          <Card>
            <CardBody>
              <Row className="mb-3 align-items-center">
                <Col md="6" lg="6" className="d-flex gap-4">
                  <div className="table-size-container">
                    <span>Showing</span>
                    <select
                      className="table-size-control"
                      value={limit}
                      onChange={(e) => {
                        setLimit(Number(e.target.value));
                        fetchUsers(Number(e.target.value), 0, "");
                      }}
                    >
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                    </select>
                    <span>entries</span>
                  </div>
                  <div className="table-search-box">
                    <input
                      type="search"
                      placeholder="Search Here..."
                      ref={keywordRef}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") searchByKeyword(e);
                      }}
                    />
                    <button disabled={loading} onClick={searchByKeyword}>
                      <i className="ri-search-line"></i>
                    </button>
                  </div>
                </Col>

                <Col md="6" lg="6" className="text-end">
                  <button
                    className="btn btn-primary"
                    onClick={() => setAddModalOpen(true)}
                  >
                    Add User
                  </button>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Table>
                    <thead>
                      <tr>
                        <th className="px-2 text-center">No</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Client Access</th>
                        <th className="text-center">Status</th>
                        <th className="text-center px-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        [...Array(15)].map((_, i) => (
                          <tr key={i}>
                            <td className="px-2 text-center"><Skeleton height={12} width={30} /></td>
                            <td><Skeleton height={12} width={80} /></td>
                            <td><Skeleton height={12} width={80} /></td>
                            <td><Skeleton height={12} width={80} /></td>
                            <td className="text-center"><Skeleton height={12} width={60} /></td>
                            <td className="text-center px-2"><Skeleton height={30} width={60} /></td>
                          </tr>
                        ))
                      ) : !users.length ? (
                        <tr><td colSpan="6" className="text-center">No users found</td></tr>
                      ) : (
                        users.map((user, i) => (
                          <tr key={user._id}>
                            <td className="px-2 text-center">{i + 1}</td>
                            <td>{user.name}</td>
                            <td>{user.email}</td>
                            <td>{clientLabel(user.clientName)}</td>
                            <td className="text-center"><span className={`badge fw-500 fs-12 ${ user.isActive ? "bg-success" : "bg-danger" }`}>{user.isActive ? "Active" : "Inactive"}</span></td>
                            <td className="px-2 text-center"><button className="btn btn-primary" onClick={() => openUserModal(user)} >View</button></td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </Col>
              </Row>
              <TablePagination limit={limit} page={page} currentRecords={users.length} total={count} loading={false} onPaginationChange={onPaginationChange}/>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)}>
        <ModalHeader toggle={() => setModalOpen(!modalOpen)}>User Details</ModalHeader>
        <ModalBody>
          {selectedUser && (
            <Form>
              <FormGroup>
                <Label>Name</Label>
                <Input value={selectedUser.name} onChange={(e) => setSelectedUser({ ...selectedUser, name: e.target.value }) }/>
              </FormGroup>
              <FormGroup>
                <Label>Email</Label>
                <Input value={selectedUser.email} onChange={(e) => setSelectedUser({ ...selectedUser, email: e.target.value }) }/>
              </FormGroup>
              <FormGroup>
                <Label>Phone</Label>
                <Input value={selectedUser.phoneNumber} onChange={(e) => setSelectedUser({ ...selectedUser, phoneNumber: e.target.value, })}/>
              </FormGroup>
              {!isClientAdmin && (
                <FormGroup>
                  <Label>Client Access</Label>
                  <Input type="select" value={selectedUser.clientName || ""} onChange={(e) => setSelectedUser({ ...selectedUser, clientName: e.target.value })}>
                    <option value="">All clients</option>
                    {clients.map((c) => (
                      <option key={c.clientName} value={c.clientName}>{c.companyName} only</option>
                    ))}
                  </Input>
                </FormGroup>
              )}
              <FormGroup switch>
                <Input type="switch" id="isActiveSwitch" checked={selectedUser.isActive} onChange={(e) => setSelectedUser({ ...selectedUser, isActive: e.target.checked, }) }/>
                <Label for="isActiveSwitch">{selectedUser.isActive ? "Active" : "Inactive"}</Label>
              </FormGroup>
            </Form>
          )}
        </ModalBody>
        <ModalFooter>
          <button className="btn btn-outline-primary" onClick={handleUpdate}>Update</button>
          <button className="btn btn-outline-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
        </ModalFooter>
      </Modal>
      <Modal isOpen={addModalOpen} toggle={() => setAddModalOpen(!addModalOpen)}>
        <ModalHeader toggle={() => setAddModalOpen(!addModalOpen)}>Add New User</ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label>Name</Label>
              <Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value }) } />
            </FormGroup>
            <FormGroup>
              <Label>Email</Label>
              <Input value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value }) }/>
            </FormGroup>
            <FormGroup>
              <Label for="password">Password</Label>
              <div className="position-relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={newUser.password}
                  onChange={(e) =>
                    setNewUser({ ...newUser, password: e.target.value })
                  }
                  placeholder="Enter password"
                />
                <button
                  type="button"
                  className="btn btn-sm btn-light position-absolute end-0 top-0 mt-1 me-1"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <i className={`ri-eye${showPassword ? "-off" : ""}-line`}></i>
                </button>
              </div>
              {!isStrongPassword(newUser.password) && newUser.password && (
                <div className="text-danger mt-1 fs-12">
                  Password must be at least 8 characters and include letters &
                  numbers.
                </div>
              )}
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm mt-2"
                onClick={generatePassword}
              >
                Generate Strong Password
              </button>
            </FormGroup>

            {!isClientAdmin && (
              <FormGroup>
                <Label>Client Access</Label>
                <Input
                  type="select"
                  value={newUser.clientName}
                  onChange={(e) =>
                    setNewUser({ ...newUser, clientName: e.target.value })
                  }
                >
                  <option value="">All clients</option>
                  {clients.map((c) => (
                    <option key={c.clientName} value={c.clientName}>
                      {c.companyName} only
                    </option>
                  ))}
                </Input>
              </FormGroup>
            )}
            <FormGroup>
              <Label>Phone</Label>
              <Input
                value={newUser.phoneNumber}
                onChange={(e) =>
                  setNewUser({ ...newUser, phoneNumber: e.target.value })
                }
              />
            </FormGroup>
            <FormGroup switch>
              <Input
                type="switch"
                id="isActiveSwitchAdd"
                checked={newUser.isActive}
                onChange={(e) =>
                  setNewUser({ ...newUser, isActive: e.target.checked })
                }
              />
              <Label for="isActiveSwitchAdd">
                {newUser.isActive ? "Active" : "Inactive"}
              </Label>
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleAddUser} disabled={loading}>
            {loading ? "Adding..." : "Submit"}
          </Button>
          <button
            className="btn btn-outline-secondary"
            onClick={() => setAddModalOpen(false)}
          >
            Cancel
          </button>
        </ModalFooter>
      </Modal>
    </Container>
  );
}
